import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { CheckCircle2, Copy, Loader2, X } from 'lucide-react';

export type ChromeWebStoreSetupPanelViewState = 'loading' | 'ready' | 'error' | 'existing';

export interface ChromeWebStoreSetupPanelViewProps {
  state: ChromeWebStoreSetupPanelViewState;
  errorMessage: string | null;
  connectionKey: string;
  bindingId: string;
  socketId: string;
  onDismiss: () => void;
  onRetry: () => void;
  onRotate: () => void;
}

export function ChromeWebStoreSetupPanelView({
  state,
  errorMessage,
  connectionKey,
  bindingId,
  socketId,
  onDismiss,
  onRetry,
  onRotate,
}: ChromeWebStoreSetupPanelViewProps) {
  const { t } = useTranslation('integrations');

  const copy = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      window.prompt(label, value);
    }
  };

  return (
    <Card className="mb-6 border-indigo-200 bg-indigo-50/40 dark:bg-indigo-950/20 dark:border-indigo-800">
      <CardHeader className="flex flex-row items-start justify-between gap-4 pb-2">
        <div>
          <CardTitle className="text-lg text-foreground">{t('chromeWebStore.title')}</CardTitle>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">{t('chromeWebStore.subtitle')}</p>
        </div>
        <Button variant="ghost" size="icon" onClick={onDismiss} aria-label={t('chromeWebStore.dismiss')}>
          <X className="h-4 w-4" />
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {state === 'loading' && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>{t('chromeWebStore.loading')}</span>
          </div>
        )}

        {state === 'error' && errorMessage && (
          <Alert variant="destructive">
            <AlertTitle>{t('chromeWebStore.errorTitle')}</AlertTitle>
            <AlertDescription>{errorMessage}</AlertDescription>
            <Button className="mt-3" variant="outline" size="sm" onClick={onRetry}>
              {t('chromeWebStore.retry')}
            </Button>
          </Alert>
        )}

        {(state === 'ready' || state === 'existing') && connectionKey && (
          <Alert className="border-green-200 bg-green-50/80 dark:bg-green-950/30 dark:border-green-800">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <AlertTitle>
              {state === 'existing' ? t('chromeWebStore.existingTitle') : t('chromeWebStore.successTitle')}
            </AlertTitle>
            <AlertDescription className="space-y-3 mt-2">
              <p className="text-sm">{t('chromeWebStore.copyInstructions')}</p>
              <div className="space-y-2">
                <label className="text-xs font-medium text-muted-foreground">
                  {t('chromeWebStore.fieldConnectionKey')}
                </label>
                <div className="flex gap-2">
                  <Input readOnly value={connectionKey} className="font-mono text-xs" />
                  <Button
                    type="button"
                    variant="secondary"
                    size="icon"
                    onClick={() => void copy(connectionKey, t('chromeWebStore.fieldConnectionKey'))}
                    aria-label={t('chromeWebStore.copyKey')}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                <label className="text-xs font-medium text-muted-foreground">
                  {t('chromeWebStore.fieldBindingId')}
                </label>
                <div className="flex gap-2">
                  <Input readOnly value={bindingId} className="font-mono text-xs" />
                  <Button
                    type="button"
                    variant="secondary"
                    size="icon"
                    onClick={() => void copy(bindingId, t('chromeWebStore.fieldBindingId'))}
                    aria-label={t('chromeWebStore.copyBinding')}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                <label className="text-xs font-medium text-muted-foreground">
                  {t('chromeWebStore.fieldSocketId')}
                </label>
                <div className="flex gap-2">
                  <Input readOnly value={socketId} className="font-mono text-xs" />
                  <Button
                    type="button"
                    variant="secondary"
                    size="icon"
                    onClick={() => void copy(socketId, t('chromeWebStore.fieldSocketId'))}
                    aria-label={t('chromeWebStore.copySocket')}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">{t('chromeWebStore.securityNote')}</p>
            </AlertDescription>
          </Alert>
        )}

        {state === 'existing' && !connectionKey && (
          <Alert>
            <AlertTitle>{t('chromeWebStore.existingNoKeyTitle')}</AlertTitle>
            <AlertDescription className="space-y-3">
              <p>{t('chromeWebStore.existingNoKeyBody')}</p>
              <Button variant="destructive" size="sm" onClick={onRotate}>
                {t('chromeWebStore.rotateBinding')}
              </Button>
            </AlertDescription>
          </Alert>
        )}

        <p className="text-xs text-muted-foreground">{t('chromeWebStore.extensionHint')}</p>
      </CardContent>
    </Card>
  );
}
